import { Enums } from '@configur-tech/upit-core-types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import ListService, {
  ListItemOutput,
  ListsItemOutput,
} from '../../services/list/ListService';
import { AppThunk } from '../store';

export interface ListsState {
  data: {
    data: ListItemOutput[];
    pagination: {
      currentPageNum: number;
      nextPageNum: number | null;
      prevPageNum: number | null;
      totalCount: number;
      totalPages: number;
    };
  };

  loading: boolean;
  error: string | null;
}

export const initialState: ListsState = {
  data: {
    data: [],
    pagination: {
      currentPageNum: 1,
      nextPageNum: null,
      prevPageNum: null,
      totalCount: 0,
      totalPages: 0,
    },
  },
  loading: false,
  error: null,
};

const ListsSlice = createSlice({
  name: 'lists',
  initialState,
  reducers: {
    // Fetch
    fetchListsStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchListsSuccess(state, action: PayloadAction<ListsItemOutput>) {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    },
    fetchListsFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const { fetchListsStart, fetchListsSuccess, fetchListsFailure } =
  ListsSlice.actions;

export default ListsSlice.reducer;

export const fetchLists =
  (token: string, query?: Record<string, unknown>): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(fetchListsStart());
      const fetched = await ListService.getLists(token, query);
      dispatch(fetchListsSuccess(fetched.data as unknown as ListsItemOutput));
    } catch (err) {
      dispatch(fetchListsFailure((err as string).toString()));
      if (
        (err as AxiosError)?.response?.data.statusCode !==
        Enums.StatusCode.NOT_FOUND
      ) {
        throw err;
      }
    }
  };

export const fetchListsForResource =
  (token: string, resourceType: string, resourceId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(fetchListsStart());
      const fetched = await ListService.getListsForResource(
        token,
        resourceType,
        resourceId,
      );
      dispatch(fetchListsSuccess(fetched.data as unknown as ListsItemOutput));
    } catch (err) {
      dispatch(fetchListsFailure((err as string).toString()));
      if (
        (err as AxiosError)?.response?.data.statusCode !==
        Enums.StatusCode.NOT_FOUND
      ) {
        throw err;
      }
    }
  };
